import type {
  AgentRun,
  AgentRunPlanStep,
  AgentRunPlanStepStatus,
} from "./types";

const PLAN_STEP_STATUSES = new Set<AgentRunPlanStepStatus>([
  "pending",
  "in_progress",
  "completed",
]);

export type LiveRunSignal = "working" | "quiet" | "stale";

export function parseLiveRunActions(value: unknown): {
  actions: string[];
  planSteps: AgentRunPlanStep[];
} {
  if (!Array.isArray(value)) return { actions: [], planSteps: [] };

  const actions: string[] = [];
  const planSteps: AgentRunPlanStep[] = [];

  for (const item of value) {
    if (typeof item === "string") {
      actions.push(item);
      continue;
    }
    if (!isRecord(item) || item.kind !== "plan_step") {
      try {
        actions.push(JSON.stringify(item));
      } catch {
        // Database JSON cannot be cyclic, but keep malformed fixture/import
        // values from breaking the whole audit list.
      }
      continue;
    }

    const label =
      typeof item.label === "string" ? item.label.trim().slice(0, 500) : "";
    const status = item.status;
    if (
      !label ||
      typeof status !== "string" ||
      !PLAN_STEP_STATUSES.has(status as AgentRunPlanStepStatus)
    ) {
      continue;
    }

    const id =
      typeof item.id === "string" && item.id.trim()
        ? item.id.trim().slice(0, 160)
        : `step-${planSteps.length + 1}`;
    const updatedAt = parseTimestamp(item.updated_at ?? item.updatedAt);
    planSteps.push({
      id,
      label,
      status: status as AgentRunPlanStepStatus,
      updatedAt,
    });
    if (status === "completed") actions.push(label);
  }

  return { actions, planSteps };
}

export function liveRunSignal(
  run: AgentRun,
  now = Date.now()
): LiveRunSignal {
  const lastSignal = [run.heartbeatAt, run.updatedAt, run.startedAt, run.createdAt]
    .find((value): value is number => typeof value === "number" && Number.isFinite(value) && value <= now);
  if (lastSignal === undefined || !Number.isFinite(now)) return "stale";
  const silenceMs = Math.max(0, now - lastSignal);
  if (silenceMs >= 15 * 60_000) return "stale";
  if (silenceMs >= 2 * 60_000) return "quiet";
  return "working";
}

/** A silent root stays visible while one of its direct subagents still has a recent signal. */
export function liveRunFamilySignal(run: AgentRun, runs: readonly AgentRun[], now = Date.now()): LiveRunSignal {
  const signals = [run, ...runs.filter((child) => child.parentRunId === run.id && child.workspaceId === run.workspaceId)]
    .filter((member) => member.status === "in_progress").map((member) => liveRunSignal(member, now));
  return signals.includes("working") ? "working" : signals.includes("quiet") ? "quiet" : "stale";
}

export function liveRunCounts(runs: readonly AgentRun[], now = Date.now()) {
  const active = runs.filter((run) => run.status === "in_progress");
  const working = active.filter((run) => liveRunSignal(run, now) === "working").length;
  const quiet = active.filter((run) => liveRunSignal(run, now) === "quiet").length;
  const stale = active.length - working - quiet;
  return { working, quiet, stale, recent: working + quiet };
}

export function partitionLiveRuns(runs: readonly AgentRun[], now = Date.now()) {
  const roots = rootRuns(runs.filter((run) => run.status === "in_progress"));
  return {
    activeRoots: roots.filter((run) => liveRunFamilySignal(run, runs, now) !== "stale"),
    staleRoots: roots.filter((run) => liveRunFamilySignal(run, runs, now) === "stale"),
    ...liveRunCounts(runs, now),
  };
}

export function currentPlanStep(run: AgentRun): AgentRunPlanStep | null {
  return (
    run.planSteps?.find((step) => step.status === "in_progress") ??
    run.planSteps?.find((step) => step.label === run.currentStep) ??
    null
  );
}

export function planProgress(run: AgentRun): {
  completed: number;
  total: number;
} {
  const steps = run.planSteps ?? [];
  return {
    completed: steps.filter((step) => step.status === "completed").length,
    total: steps.length,
  };
}

export function rootRuns(runs: readonly AgentRun[]): AgentRun[] {
  const ids = new Set(runs.map((run) => run.id));
  return runs.filter((run) => !run.parentRunId || !ids.has(run.parentRunId));
}

export function childRuns(
  runs: readonly AgentRun[],
  parentRunId: string
): AgentRun[] {
  return runs
    .filter((run) => run.parentRunId === parentRunId)
    .sort((a, b) => a.startedAt - b.startedAt);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function parseTimestamp(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}
